import { TableColumn } from '@shared/components/data-table/data-table.component';
import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { PaymentSearchResultDTO } from '../../../../api/payment-service/model/models';

// ============================================================================
// INTERFACES - Using API DTOs
// ============================================================================

export interface PaymentHistory extends PaymentSearchResultDTO {
  // Extend the API DTO with any additional frontend-specific properties if needed
}

export interface PaymentHistorySearchParams {
  orderCode?: string;
  status?: string;
  pageRequest: {
    page: number;
    size: number;
    sortBy?: string;
    ascending?: boolean;
  };
}

// ============================================================================
// ENUMS AND CONSTANTS - Using API Enums
// ============================================================================

export const PAYMENT_STATUS = {
  PENDING: 'PENDING',
  SETTLED: 'SETTLED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
} as const;

export type PaymentStatus =
  (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

export const PAYMENT_METHOD = {
  CASH: 'CASH',
  BANK_TRANSFER: 'BANK_TRANSFER',
  CARD: 'CARD',
  E_WALLET: 'E_WALLET',
  COD: 'COD',
} as const;

export type PaymentMethod =
  (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD];

export const PAYMENT_PLATFORM = {
  MOMO: 'MOMO',
  ZALOPAY: 'ZALOPAY',
  VNPAY: 'VNPAY',
  STRIPE: 'STRIPE',
  NONE: 'NONE',
} as const;

export type PaymentPlatform =
  (typeof PAYMENT_PLATFORM)[keyof typeof PAYMENT_PLATFORM];

// ============================================================================
// OPTIONS AND CONSTANTS
// ============================================================================

export const PAYMENT_STATUS_OPTIONS = [
  { label: 'payment_history.status.pending', value: PAYMENT_STATUS.PENDING },
  { label: 'payment_history.status.settled', value: PAYMENT_STATUS.SETTLED },
  { label: 'payment_history.status.failed', value: PAYMENT_STATUS.FAILED },
  {
    label: 'payment_history.status.cancelled',
    value: PAYMENT_STATUS.CANCELLED,
  },
  { label: 'payment_history.status.refunded', value: PAYMENT_STATUS.REFUNDED },
];

export const PAYMENT_METHOD_OPTIONS = [
  { label: 'payment_history.methods.cash', value: PAYMENT_METHOD.CASH },
  {
    label: 'payment_history.methods.bank_transfer',
    value: PAYMENT_METHOD.BANK_TRANSFER,
  },
  { label: 'payment_history.methods.card', value: PAYMENT_METHOD.CARD },
  { label: 'payment_history.methods.e_wallet', value: PAYMENT_METHOD.E_WALLET },
  { label: 'payment_history.methods.cod', value: PAYMENT_METHOD.COD },
];

export const PAYMENT_PLATFORM_OPTIONS = [
  { label: 'payment_history.platforms.momo', value: PAYMENT_PLATFORM.MOMO },
  {
    label: 'payment_history.platforms.zalopay',
    value: PAYMENT_PLATFORM.ZALOPAY,
  },
  { label: 'payment_history.platforms.vnpay', value: PAYMENT_PLATFORM.VNPAY },
  { label: 'payment_history.platforms.stripe', value: PAYMENT_PLATFORM.STRIPE },
  { label: 'payment_history.platforms.none', value: PAYMENT_PLATFORM.NONE },
];

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

export const PAYMENT_HISTORY_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'payment_history.table.columns.order_code',
    prop: 'orderCode',
    width: 150,
    type: 'text',
  },
  {
    label: 'payment_history.table.columns.amount',
    prop: 'amount',
    width: 120,
    type: 'custom',
    align: 'right',
    styleClass: 'amount-cell',
  },
  {
    label: 'payment_history.table.columns.currency',
    prop: 'currency',
    width: 80,
    type: 'text',
  },
  {
    label: 'payment_history.table.columns.pay_type',
    prop: 'payType',
    width: 120,
    type: 'custom',
  },
  {
    label: 'payment_history.table.columns.platform',
    prop: 'platform',
    width: 120,
    type: 'custom',
  },
  {
    label: 'payment_history.table.columns.status',
    prop: 'status',
    width: 120,
    type: 'status',
  },
  {
    label: 'payment_history.table.columns.provider_reference',
    prop: 'providerReference',
    width: 150,
    type: 'text',
  },
  {
    label: 'payment_history.table.columns.creation_date',
    prop: 'creationDate',
    width: 180,
    type: 'custom',
  },
  {
    label: 'payment_history.table.columns.last_update_date',
    prop: 'lastUpdateDate',
    width: 180,
    type: 'custom',
  },
  {
    label: 'payment_history.table.columns.actions',
    prop: 'actions',
    width: 200,
    type: 'actions',
  },
];

// ============================================================================
// SEARCH CONFIGURATIONS
// ============================================================================

export const PAYMENT_HISTORY_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'orderCode',
    label: 'payment_history.search.fields.order_code',
    type: 'text',
    placeholder: 'payment_history.search.placeholders.enter_order_code',
  },
  {
    key: 'status',
    label: 'payment_history.search.fields.status',
    type: 'select',
    placeholder: 'payment_history.search.placeholders.select_status',
    options: PAYMENT_STATUS_OPTIONS,
    showClear: true,
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function formatCurrency(
  amount: number,
  currency: string = 'VND'
): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

// Note: formatDate function moved to global date-formatter.util.ts
// Use DateFormatterService for locale-aware date formatting

export function getPaymentStatusDisplayText(status: string): string {
  const statusMap: { [key: string]: string } = {
    [PAYMENT_STATUS.PENDING]: 'payment_history.status.pending',
    [PAYMENT_STATUS.SETTLED]: 'payment_history.status.settled',
    [PAYMENT_STATUS.FAILED]: 'payment_history.status.failed',
    [PAYMENT_STATUS.CANCELLED]: 'payment_history.status.cancelled',
    [PAYMENT_STATUS.REFUNDED]: 'payment_history.status.refunded',
  };
  return statusMap[status] || status;
}

export function getPaymentMethodDisplayText(method: string): string {
  const methodMap: { [key: string]: string } = {
    [PAYMENT_METHOD.CASH]: 'payment_history.methods.cash',
    [PAYMENT_METHOD.BANK_TRANSFER]: 'payment_history.methods.bank_transfer',
    [PAYMENT_METHOD.CARD]: 'payment_history.methods.card',
    [PAYMENT_METHOD.E_WALLET]: 'payment_history.methods.e_wallet',
    [PAYMENT_METHOD.COD]: 'payment_history.methods.cod',
  };
  return methodMap[method] || method;
}

export function getPaymentPlatformDisplayText(platform: string): string {
  const platformMap: { [key: string]: string } = {
    [PAYMENT_PLATFORM.MOMO]: 'payment_history.platforms.momo',
    [PAYMENT_PLATFORM.ZALOPAY]: 'payment_history.platforms.zalopay',
    [PAYMENT_PLATFORM.VNPAY]: 'payment_history.platforms.vnpay',
    [PAYMENT_PLATFORM.STRIPE]: 'payment_history.platforms.stripe',
    [PAYMENT_PLATFORM.NONE]: 'payment_history.platforms.none',
  };
  return platformMap[platform] || platform;
}

export function getPaymentStatusSeverity(
  status: string
): 'success' | 'secondary' | 'warning' | 'danger' | 'info' {
  const severityMap: {
    [key: string]: 'success' | 'secondary' | 'warning' | 'danger' | 'info';
  } = {
    [PAYMENT_STATUS.PENDING]: 'warning',
    [PAYMENT_STATUS.SETTLED]: 'success',
    [PAYMENT_STATUS.FAILED]: 'danger',
    [PAYMENT_STATUS.CANCELLED]: 'secondary',
    [PAYMENT_STATUS.REFUNDED]: 'info',
  };
  return severityMap[status] || 'secondary';
}

export function getPaymentStatusIcon(status: string): string {
  const iconMap: { [key: string]: string } = {
    [PAYMENT_STATUS.PENDING]: 'pi pi-clock text-yellow-500',
    [PAYMENT_STATUS.SETTLED]: 'pi pi-check-circle text-green-500',
    [PAYMENT_STATUS.FAILED]: 'pi pi-times-circle text-red-500',
    [PAYMENT_STATUS.CANCELLED]: 'pi pi-ban text-gray-500',
    [PAYMENT_STATUS.REFUNDED]: 'pi pi-refresh text-blue-500',
  };
  return iconMap[status] || 'pi pi-question-circle text-gray-500';
}

export function getPaymentStatusStyle(status: string): any {
  const styleMap: { [key: string]: any } = {
    [PAYMENT_STATUS.PENDING]: {
      backgroundColor: '#fef3c7',
      color: '#a16207',
      border: 'none',
    },
    [PAYMENT_STATUS.SETTLED]: {
      backgroundColor: '#ecfdf5',
      color: '#047857',
      border: 'none',
    },
    [PAYMENT_STATUS.FAILED]: {
      backgroundColor: '#fef2f2',
      color: '#dc2626',
      border: 'none',
    },
    [PAYMENT_STATUS.CANCELLED]: {
      backgroundColor: '#f9fafb',
      color: '#6b7280',
      border: 'none',
    },
    [PAYMENT_STATUS.REFUNDED]: {
      backgroundColor: '#f0f9ff',
      color: '#0284c7',
      border: 'none',
    },
  };
  return (
    styleMap[status] || {
      backgroundColor: '#f9fafb',
      color: '#6b7280',
      border: 'none',
    }
  );
}
